/* ProteinPlus theme — vanilla JS, no dependencies */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  /* Prices follow the shop's own money format (same as Liquid's | money), not the browser locale */
  const money = (cents) => {
    const f = (window.theme && theme.moneyFormat) || '{{amount_with_comma_separator}} €';
    const c = Math.round(Number(cents) || 0);
    const parts = (sep, dec, decimals = 2) => {
      const [i, d] = (c / 100).toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dec + d : '');
    };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: parts(',', '.'), amount_no_decimals: parts(',', '.', 0),
      amount_with_comma_separator: parts('.', ','), amount_no_decimals_with_comma_separator: parts('.', ',', 0),
      amount_with_space_separator: parts(' ', ','), amount_with_apostrophe_separator: parts("'", '.'),
    }[k] ?? parts(',', '.')));
  };

  /* ---------------- Drawers (menu + cart) ---------------- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => $('.drawer__panel', this)?.focus(), 50);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this.opener?.focus?.();
    }
  }
  customElements.define('menu-drawer', class extends Drawer {});

  class CartDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        if (btn) updateLine(btn.dataset.line, parseInt(btn.dataset.qtyChange, 10));
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) updateLine(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
      });
    }
    /** Replace drawer contents with freshly rendered section HTML */
    renderFrom(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('.drawer__panel', doc);
      const panel = $('.drawer__panel', this);
      if (fresh && panel) panel.innerHTML = fresh.innerHTML;
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      document.getElementById(opener.dataset.drawerOpen)?.open(opener);
      return;
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer' && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      $('cart-drawer')?.open(cartToggle);
    }
  });

  /* ---------------- Cart API ---------------- */
  const setCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.dataset.count = count; });
  };

  async function updateLine(line, quantity) {
    const drawer = $('cart-drawer');
    const onCartPage = document.body.classList.contains('template-cart');
    drawer?.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (onCartPage) return window.location.reload();
      setCount(data.item_count);
      drawer?.renderFrom(data.sections?.['cart-drawer']);
    } catch (err) {
      console.error(err);
    } finally {
      drawer?.classList.remove('is-loading');
    }
  }

  /* Product forms: add to cart via AJAX, then open the drawer */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const perso = $('name-personalizer', form);
    if (perso && !perso.validate()) return;
    const buttons = $$(`[data-add-button]`).filter((b) => b.form === form || form.contains(b));
    const errorEl = $('[data-form-error]', form);
    if (buttons.some((b) => b.disabled)) return;
    buttons.forEach((b) => b.classList.add('is-loading'));
    if (errorEl) errorEl.hidden = true;

    const body = new FormData(form);
    // UI-only fields (option pickers, line-count toggle) must not reach the cart
    [...body.keys()].filter((k) => k.startsWith('opt-') || k.startsWith('perso-lines') || k.startsWith('purchase-') || k.startsWith('bundle-') || k.startsWith('bh-')).forEach((k) => body.delete(k));
    // When the theme's own subscription picker is used, the subscription app's injected widget (Seal) is hidden:
    // drop its fields so only one selling_plan reaches the cart.
    const planInput = $('purchase-options [data-selling-plan]', form);
    if (planInput) {
      [...body.keys()].filter((k) => k.startsWith('subs_')).forEach((k) => body.delete(k));
      body.delete('selling_plan');
      if (!planInput.disabled && planInput.value) body.append('selling_plan', planInput.value);
    }
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);
    // A pack with different heights per pillow goes in as one cart line per height
    const packItems = $('bundle-picker', form)?.items?.();
    const request = packItems
      ? { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: packItems, sections: 'cart-drawer', sections_url: window.location.pathname }) }
      : { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body };
    try {
      const res = await fetch(`${theme.routes.cartAdd}.js`, request);
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      if (theme.cartType === 'page') { window.location.href = theme.routes.cart; return; }
      const drawer = $('cart-drawer');
      drawer?.renderFrom(data.sections?.['cart-drawer']);
      const cart = await fetch(`${theme.routes.cart}.js`).then((r) => r.json());
      setCount(cart.item_count);
      drawer?.open();
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
    } finally {
      buttons.forEach((b) => b.classList.remove('is-loading'));
    }
  });

  /* ---------------- Quantity input ---------------- */
  customElements.define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      const input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (!btn) return;
        const min = parseInt(input.min, 10) || 1;
        input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(btn.dataset.step, 10));
      });
    }
  });

  /* ---------------- Media gallery ---------------- */
  customElements.define('media-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.prev = $('[data-gallery-prev]', this);
      this.next = $('[data-gallery-next]', this);
      if (!this.track) return;
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb))));
      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.sync();
    }
    get count() { return this.track.children.length; }
    go(i) {
      const idx = (i + this.count) % this.count;
      this.track.scrollTo({ left: this.track.clientWidth * idx, behavior: 'smooth' });
    }
    goToMedia(mediaId) {
      const slides = Array.from(this.track.children);
      const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.go(i);
    }
    sync() {
      this.index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
      const active = this.thumbs[this.index];
      if (active) active.parentElement.scrollTo({ left: active.offsetLeft - 40, behavior: 'smooth' });
      // pause any video that scrolled away
      $$('video', this.track).forEach((v, i) => { if (i !== this.index) v.pause?.(); });
    }
  });

  /* ---------------- Variant picker ---------------- */
  customElements.define('variant-picker', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this)?.textContent || '[]');
      this.section = this.closest('[data-section]') || document;
      this.addEventListener('change', () => this.update());
    }
    update() {
      const selected = $$('input[type="radio"]:checked', this).map((i) => i.value);
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      const fieldsets = $$('fieldset', this);
      fieldsets.forEach((fs, i) => { const l = $('[data-option-label]', fs); if (l) l.textContent = selected[i]; });
      const idInput = $('[data-variant-id]', this.section);
      const buttons = $$('[data-add-button]');
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; const l = $('[data-add-label]', b); if (l) l.textContent = theme.strings.unavailable; });
        return;
      }
      if (idInput) idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const l = $('[data-add-label]', b);
        if (!l) return;
        if (!l.dataset.original) l.dataset.original = l.textContent.trim();
        l.textContent = variant.available ? l.dataset.original : theme.strings.soldOut;
      });
      $$('[data-price-current]').forEach((el) => { el.textContent = money(variant.price); });
      $$('[data-price-compare]').forEach((el) => {
        el.hidden = !(variant.compare_at_price > variant.price);
        if (!el.hidden) el.textContent = money(variant.compare_at_price);
      });
      if (variant.featured_media) $('media-gallery')?.goToMedia(variant.featured_media.id);
    }
  });

  /* ---------------- Product options (model thumbnails + formula cards) ---------------- */

  customElements.define('product-options', class extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse($('[data-variants]', this)?.textContent || '[]');
      this.form = this.closest('form');
      this.idInput = this.form && $('[data-variant-id]', this.form);
      const current = this.variants.find((v) => String(v.id) === this.idInput?.value) || this.variants[0];
      this.fallback = current ? current.options.slice() : [];
      this.addEventListener('change', (e) => { if (e.target.closest('.opt-group')) this.update(true); });
      this.update(false);
    }
    selected() {
      const opts = this.fallback.slice();
      $$('.opt-group', this).forEach((g) => {
        const r = $('input[type="radio"]:checked', g);
        if (r) opts[Number(g.dataset.optionIndex)] = r.value;
      });
      return opts;
    }
    find(opts) { return this.variants.find((v) => v.options.every((o, i) => o === opts[i])); }
    update(userChange) {
      const opts = this.selected();
      const variant = this.find(opts);
      $$('.opt-group', this).forEach((g) => {
        const label = $('[data-option-label]', g);
        if (label) label.textContent = opts[Number(g.dataset.optionIndex)];
      });
      this.renderFormulas(opts);
      const buttons = $$('[data-add-button]').filter((b) => b.form === this.form);
      if (!variant) {
        buttons.forEach((b) => { b.disabled = true; const l = $('[data-add-label]', b); if (l) l.textContent = theme.strings.unavailable; });
        return;
      }
      this.fallback = variant.options.slice();
      if (this.idInput) this.idInput.value = variant.id;
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        const l = $('[data-add-label]', b);
        if (!l) return;
        if (!l.dataset.original) l.dataset.original = l.textContent.trim();
        l.textContent = variant.available ? l.dataset.original : theme.strings.soldOut;
      });
      $$('[data-price-current]').forEach((el) => { el.textContent = money(variant.price); });
      const onSale = variant.compare_at_price > variant.price;
      $$('[data-price-compare]').forEach((el) => { el.hidden = !onSale; if (onSale) el.textContent = money(variant.compare_at_price); });
      $$('[data-price-save]').forEach((el) => {
        el.hidden = !onSale;
        if (onSale) el.textContent = `-${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)} %`;
      });
      $('purchase-options', this)?.setVariant?.(variant);
      $('bundle-picker', this)?.setVariant?.(variant);
      if (userChange) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.featured_media) $('media-gallery')?.goToMedia(variant.featured_media.id);
      }
    }
    /* Each formula card shows its own price for the chosen model, plus the saving vs buying refills separately */
    renderFormulas(opts) {
      $$('[data-formula]', this).forEach((g) => {
        const idx = Number(g.dataset.optionIndex);
        const refillValue = Number(g.dataset.refillValue) || 0;
        const cards = $$('input[type="radio"]', g);
        const priceFor = (value) => { const o = opts.slice(); o[idx] = value; return this.find(o); };
        const base = priceFor(cards[0]?.value);
        cards.forEach((input) => {
          const card = input.closest('.formula-card');
          const v = priceFor(input.value);
          const priceEl = $('[data-formula-price]', card);
          const saveEl = $('[data-formula-save]', card);
          card.classList.toggle('is-unavailable', !v || !v.available);
          if (priceEl) priceEl.textContent = v ? money(v.price) : '—';
          if (!saveEl) return;
          const refills = Number(input.dataset.refills) || 0;
          const saving = v && base ? refills * refillValue - (v.price - base.price) : 0;
          saveEl.hidden = saving <= 0;
          if (saving > 0) saveEl.textContent = `${theme.strings.save} ${money(saving)}`;
        });
      });
    }
  });

  /* ---------------- One-time vs subscription (native selling plans) ---------------- */
  customElements.define('purchase-options', class extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.planInput = $('[data-selling-plan]', this);
      this.addEventListener('change', () => this.sync());
      this.sync();
    }
    setVariant(variant) {
      $$('input[type="radio"]', this).forEach((r) => {
        if (!r.value) { r.dataset.price = variant.price; r.dataset.compare = variant.compare_at_price || 0; return; }
        const alloc = (variant.selling_plan_allocations || []).find((x) => String(x.selling_plan_id) === r.value);
        if (alloc) { r.dataset.price = alloc.price; r.dataset.compare = alloc.compare_at_price || 0; }
        const strong = $('.formula-card__price strong', r.closest('.formula-card'));
        if (strong) strong.textContent = money(Number(r.dataset.price));
      });
      this.sync();
    }
    sync() {
      const r = $('input[type="radio"]:checked', this);
      if (!r || !this.planInput) return;
      this.planInput.value = r.value;
      this.planInput.disabled = !r.value;              // one-time purchase: no selling_plan sent
      const price = Number(r.dataset.price);
      const compare = Number(r.dataset.compare) || 0;
      const scope = [this.form, ...$$(`sticky-atc[data-form="${this.form?.getAttribute('id')}"]`)].filter(Boolean);
      scope.forEach((root) => {
        $$('[data-price-current]', root).forEach((el) => { el.textContent = money(price); });
        $$('[data-price-compare]', root).forEach((el) => { el.hidden = !(compare > price); if (compare > price) el.textContent = money(compare); });
        $$('[data-price-save]', root).forEach((el) => {
          el.hidden = !(compare > price);
          if (compare > price) el.textContent = `-${Math.round((compare - price) * 100 / compare)} %`;
        });
      });
      $('bundle-picker', this.form)?.render?.();
    }
  });

  /* ---------------- Pack picker (1 / 2 / 4) ----------------
     Each card shows the pack price (unit price x qty - pack discount). For packs of 2+, one row per item lets the
     buyer pick a different option value (height) for each; the main picker's value is the default for every row.
     The discount itself comes from Shopify automatic discounts, the theme only displays it. */
  customElements.define('bundle-picker', class extends HTMLElement {
    connectedCallback() {
      this.po = this.closest('product-options');
      this.variants = JSON.parse($('[data-variants]', this.po || document)?.textContent || '[]');
      this.idx = Number(this.dataset.optionIndex);
      this.qtyInput = $('[data-bundle-qty]', this);
      this.rows = $('[data-bundle-heights]', this);
      const idInput = this.closest('form')?.querySelector('[data-variant-id]');
      this.variant = this.variants.find((v) => String(v.id) === idInput?.value) || this.variants[0];
      this.values = this.idx >= 0 ? [...new Set(this.variants.map((v) => v.options[this.idx]))] : [];
      this.heights = [];
      this.addEventListener('change', (e) => {
        if (e.target.name?.startsWith('bundle-')) { this.render(); return; }
        const m = e.target.name?.match(/^bh-(\d+)$/);
        if (m) this.heights[Number(m[1])] = e.target.value;
      });
      this.render();
    }
    qty() { return Number($('input[name^="bundle-"]:checked', this)?.value) || 1; }
    variantFor(value) {
      if (!this.variant) return null;
      return this.variants.find((v) => v.options.every((o, i) => (i === this.idx ? o === value : o === this.variant.options[i])));
    }
    setVariant(variant) {
      this.variant = variant;
      this.heights = [];
      this.render();
    }
    render() {
      const qty = this.qty();
      if (this.qtyInput) this.qtyInput.value = qty;
      const v = this.variant;
      if (v) {
        // unit price = the selected purchase option (subscription or one-time) when the form has one
        const po = $('purchase-options input[type="radio"]:checked', this.closest('form') || document);
        const unit = po && po.dataset.price ? Number(po.dataset.price) : v.price;
        // savings are measured against the regular one-time price, not the compare-at price
        const unitCompare = v.price;
        $$('input[name^="bundle-"]', this).forEach((r) => {
          const card = r.closest('.bundle-card');
          const q = Number(r.value);
          const total = unit * q - (Number(r.dataset.discount) || 0);
          const was = Math.max(unit, unitCompare) * q;
          $('[data-bundle-total]', card).textContent = money(total);
          const wasEl = $('[data-bundle-was]', card);
          const saveEl = $('[data-bundle-save]', card);
          saveEl.hidden = !(was > total);
          if (wasEl) wasEl.hidden = !(was > total);
          if (was > total) { wasEl && (wasEl.textContent = money(was)); saveEl.textContent = `${this.dataset.saveLabel} ${money(was - total)}`; }
          const unitEl = $('[data-bundle-unit]', card);
          if (unitEl) unitEl.textContent = money(Math.round(total / q));
          const unitWasEl = $('[data-bundle-unit-was]', card);
          if (unitWasEl) { unitWasEl.hidden = !(was > total); unitWasEl.textContent = money(Math.round(was / q)); }
        });
      }
      // one height row per item (packs of 2+ only)
      const base = v ? v.options[this.idx] : null;
      if (this.idx < 0 || qty < 2 || !base) { this.rows.hidden = true; this.rows.innerHTML = ''; return; }
      this.heights = Array.from({ length: qty }, (_, i) => this.heights[i] || base);
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      this.rows.innerHTML = this.heights.map((h, i) => `
        <div class="bundle-row"><span class="bundle-row__label">${esc(this.dataset.unitLabel)} ${i + 1}</span>
          <div class="bundle-row__opts">${this.values.map((val) => {
            const ok = this.variantFor(val)?.available;
            return `<label class="bundle-chip${ok ? '' : ' is-off'}"><input type="radio" class="visually-hidden" name="bh-${i}" value="${esc(val)}"${val === h ? ' checked' : ''}${ok ? '' : ' disabled'}><span>${esc(val)}</span></label>`;
          }).join('')}</div></div>`).join('');
      this.rows.hidden = false;
    }
    /* null = normal single-line add (id + quantity from the form) */
    items() {
      const qty = this.qty();
      if (this.idx < 0 || qty < 2) return null;
      const counts = new Map();
      this.heights.slice(0, qty).forEach((h) => {
        const v = this.variantFor(h);
        if (v) counts.set(v.id, (counts.get(v.id) || 0) + 1);
      });
      return counts.size ? [...counts].map(([id, quantity]) => ({ id, quantity })) : null;
    }
  });

  /* ---------------- Name personaliser + live stamp preview ---------------- */
  const stripEmoji = (s) => s.replace(/\p{Extended_Pictographic}|️|‍/gu, '').replace(/ {2,}/g, ' ');
  customElements.define('name-personalizer', class extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 12;
      this.line2 = $('[data-perso-line2]', this);
      this.addEventListener('input', (e) => {
        const input = e.target.closest('[data-perso-line]');
        if (!input) return;
        const clean = stripEmoji(input.value).slice(0, this.max);
        if (clean !== input.value) input.value = clean;
        $('[data-perso-error]', this).hidden = true;
        this.render();
      });
      this.addEventListener('change', (e) => {
        if (e.target.name?.startsWith('perso-lines')) this.setLines(Number(e.target.value));
        if (e.target.dataset.icon !== undefined) this.render();
      });
      this.addEventListener('click', (e) => {
        const b = e.target.closest('[data-fabric-btn]');
        if (!b) return;
        const preview = $('.stamp-preview', this);
        preview.dataset.fabric = b.dataset.fabricBtn;
        $$('[data-fabric-btn]', this).forEach((x) => x.classList.toggle('is-active', x === b));
      });
      this.render();
    }
    setLines(n) {
      if (!this.line2) return;
      const input = $('input', this.line2);
      this.line2.hidden = n < 2;
      input.disabled = n < 2;
      if (n < 2) input.value = '';
      this.render();
    }
    render() {
      [1, 2].forEach((n) => {
        const input = $(`[data-perso-line="${n}"]`, this);
        const count = $(`[data-perso-count="${n}"]`, this);
        const out = $(`[data-preview-line="${n}"]`, this);
        if (!input) return;
        if (count) count.textContent = `${input.value.length}/${this.max}`;
        if (!out) return;
        if (n === 1) out.textContent = input.value.trim() || input.placeholder;
        else { out.textContent = input.value.trim(); out.hidden = input.disabled || !input.value.trim(); }
      });
      const icon = $('input[data-icon]:checked', this)?.dataset.icon || '';
      const holder = $('[data-preview-icon]', this);
      if (holder) {
        holder.hidden = !icon;
        $$('[data-icon-svg]', holder).forEach((s) => { s.hidden = s.dataset.iconSvg !== icon; });
      }
    }
    validate() {
      const input = $('[data-perso-line="1"]', this);
      if (input && !input.value.trim()) {
        $('[data-perso-error]', this).hidden = false;
        input.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      return true;
    }
  });

  /* ---------------- Estimated ship date (business days, same-day cutoff) ---------------- */
  customElements.define('ship-date', class extends HTMLElement {
    connectedCallback() {
      const d = new Date();
      let days = Number(this.dataset.days) || 0;
      if (d.getHours() >= (Number(this.dataset.cutoff) || 14)) days += 1;
      const isWeekend = (x) => x.getDay() === 0 || x.getDay() === 6;
      while (isWeekend(d)) d.setDate(d.getDate() + 1);
      while (days > 0) { d.setDate(d.getDate() + 1); if (!isWeekend(d)) days -= 1; }
      const el = $('[data-ship-date]', this);
      if (el) el.textContent = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    }
  });

  /* ---------------- Sticky add to cart ---------------- */
  customElements.define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      const target = $('[data-main-atc]');
      const footer = $('.footer');
      if (!target) return;
      let pastButton = false, atFooter = false;
      const render = () => {
        const show = pastButton && !atFooter;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
      };
      new IntersectionObserver(([entry]) => {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        render();
      }).observe(target);
      if (footer) new IntersectionObserver(([entry]) => { atFooter = entry.isIntersecting; render(); }).observe(footer);
    }
  });

  /* ---------------- Carousel ---------------- */
  customElements.define('carousel-slider', class extends HTMLElement {
    connectedCallback() {
      const track = $('[data-carousel-track]', this);
      const prev = $('[data-carousel-prev]', this);
      const next = $('[data-carousel-next]', this);
      if (!track) return;
      const step = () => (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + 14;
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max;
        const nav = $('.carousel__nav', this);
        if (nav) nav.hidden = track.scrollWidth <= track.clientWidth + 4;
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    }
  });

  /* ---------------- Before / after ---------------- */
  customElements.define('before-after', class extends HTMLElement {
    connectedCallback() {
      const range = $('.ba__range', this);
      range?.addEventListener('input', () => this.style.setProperty('--pos', `${range.value}%`));
    }
  });

  /* ---------------- Announcement bar ---------------- */
  customElements.define('announcement-bar', class extends HTMLElement {
    connectedCallback() {
      const slides = $$('.announcement__slide', this);
      if (slides.length < 2) return;
      let i = 0;
      setInterval(() => {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, Number(this.dataset.interval) || 4000);
    }
  });

  customElements.define('countdown-timer', class extends HTMLElement {
    connectedCallback() {
      const end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(end)) return;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const diff = end - Date.now();
        if (diff <= 0) { this.hidden = true; clearInterval(this.timer); return; }
        this.hidden = false;
        const s = Math.floor(diff / 1000);
        $('[data-d]', this).textContent = pad(Math.floor(s / 86400));
        $('[data-h]', this).textContent = pad(Math.floor((s % 86400) / 3600));
        $('[data-m]', this).textContent = pad(Math.floor((s % 3600) / 60));
        $('[data-s]', this).textContent = pad(s % 60);
      };
      tick();
      this.timer = setInterval(tick, 1000);
    }
  });

  /* ---------------- Videos ---------------- */
  document.addEventListener('click', (e) => {
    const bubble = e.target.closest('[data-video-open]');
    if (bubble) {
      const modal = $('[data-video-modal]');
      const video = $('video', modal);
      video.src = bubble.dataset.videoOpen;
      modal.showModal();
      video.play().catch(() => {});
      return;
    }
    if (e.target.closest('[data-video-close]') || e.target.matches('[data-video-modal]')) {
      const modal = $('[data-video-modal]');
      $('video', modal).pause();
      modal.close();
      return;
    }
    const card = e.target.closest('[data-video-card]');
    if (card) {
      const v = $('video', card);
      $$('[data-video-card] video').forEach((o) => { if (o !== v) { o.pause(); o.closest('[data-video-card]').classList.remove('is-playing'); } });
      if (v.paused) { v.muted = false; v.play(); card.classList.add('is-playing'); } else { v.pause(); card.classList.remove('is-playing'); }
    }
  });

  /* ---------------- Header state + reveal on scroll ---------------- */
  const header = $('[data-header]');
  if (header) {
    const setH = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setH();
    window.addEventListener('resize', setH);
    window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 8), { passive: true });
  }

  if (!document.body.classList.contains('no-anim') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    $$('.reveal').forEach((el) => io.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  // Theme editor: re-run reveals for sections added/changed live
  document.addEventListener('shopify:section:load', (e) => $$('.reveal', e.target).forEach((el) => el.classList.add('is-in')));
})();
